import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Volume2, VolumeX, Play, Pause, Save, ArrowLeft, Layers, Music, Drum, Guitar, Sparkles, ShoppingCart } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Slider } from "../components/ui/slider";
import { toast } from "sonner";
import { useCart } from "../App";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const STEM_ICONS = {
  full: Music,
  drums: Drum,
  bass: Guitar,
  melody: Sparkles,
  fx: Layers,
};

const STEM_COLORS = {
  full: "#00f3ff",
  drums: "#ff00ff",
  bass: "#00ff88",
  melody: "#ffaa00",
  fx: "#aa66ff",
};

export default function BeatCustomizerPage() {
  const { beatId } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [beatData, setBeatData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [stemConfig, setStemConfig] = useState([]);
  const [mixName, setMixName] = useState("My Custom Mix");
  const audioRef = useRef(null);
  const token = localStorage.getItem("token");

  useEffect(() => {
    const fetchStems = async () => {
      try {
        const res = await axios.get(`${API}/api/customizer/${beatId}/stems`);
        setBeatData(res.data);
        setStemConfig(res.data.stems.map(s => ({ ...s, volume: s.volume || 100, muted: false })));
      } catch {
        toast.error("Failed to load beat data");
      } finally {
        setLoading(false);
      }
    };
    fetchStems();
  }, [beatId]);

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(() => {});
    }
    setPlaying(!playing);
  };

  const toggleMute = (index) => {
    setStemConfig(prev => prev.map((s, i) => i === index ? { ...s, muted: !s.muted } : s));
  };

  const setVolume = (index, val) => {
    setStemConfig(prev => prev.map((s, i) => i === index ? { ...s, volume: val[0] } : s));
  };

  const saveMix = async () => {
    if (!token) { toast.error("Sign in to save mixes"); return; }
    try {
      await axios.post(`${API}/api/customizer/${beatId}/save-mix`,
        { stem_config: stemConfig, name: mixName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Mix saved!");
    } catch { toast.error("Failed to save mix"); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center pt-20">
        <div className="w-8 h-8 border-2 border-neon-cyan border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!beatData) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center pt-20">
        <p className="text-zinc-400">Beat not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark pt-24 pb-16 px-4" data-testid="beat-customizer-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="customizer-back">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl uppercase tracking-tight">Beat Customizer</h1>
            <p className="text-zinc-500 text-sm">Mix and match stems before you buy</p>
          </div>
        </div>

        {/* Beat Info Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass border border-white/10 rounded-xl overflow-hidden mb-8"
        >
          <div className="flex flex-col sm:flex-row gap-6 p-6">
            <div className="w-full sm:w-48 h-48 rounded-lg overflow-hidden bg-zinc-800 flex-shrink-0">
              {beatData.image_url ? (
                <img src={beatData.image_url} alt={beatData.title} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-neon-cyan/20 to-neon-pink/20">
                  <Music className="w-12 h-12 text-zinc-600" />
                </div>
              )}
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-white mb-2" data-testid="customizer-beat-title">{beatData.title}</h2>
              <div className="flex gap-2 mb-4">
                {beatData.genre && <Badge className="bg-neon-magenta/20 text-neon-magenta border-0">{beatData.genre}</Badge>}
                {beatData.bpm > 0 && <Badge className="bg-white/10 text-zinc-300 border-0">{beatData.bpm} BPM</Badge>}
                {beatData.key && <Badge className="bg-white/10 text-zinc-300 border-0">Key: {beatData.key}</Badge>}
              </div>

              {/* Master Play */}
              <div className="flex items-center gap-4">
                <audio ref={audioRef} src={beatData.stems[0]?.url || ""} onEnded={() => setPlaying(false)} />
                <Button
                  onClick={togglePlay}
                  className={`${playing ? "bg-neon-magenta" : "bg-neon-cyan"} text-black font-bold px-6`}
                  data-testid="customizer-play-btn"
                >
                  {playing ? <Pause className="w-4 h-4 mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                  {playing ? "Pause" : "Preview Mix"}
                </Button>
                <Badge className="bg-neon-cyan/10 text-neon-cyan border border-neon-cyan/30 text-sm">
                  From ${beatData.basic_price}
                </Badge>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Stem Mixer */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass border border-white/10 rounded-xl p-6 mb-8"
        >
          <h3 className="font-heading text-lg uppercase tracking-wider mb-6 flex items-center gap-2">
            <Layers className="w-5 h-5 text-neon-cyan" />
            Stem Mixer
          </h3>

          <div className="space-y-4">
            {stemConfig.map((stem, i) => {
              const Icon = STEM_ICONS[stem.type] || Music;
              const stemColor = STEM_COLORS[stem.type] || "#00f3ff";
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className={`flex items-center gap-4 p-4 rounded-lg transition-all ${
                    stem.muted ? "bg-zinc-800/30 opacity-50" : "bg-zinc-800/60"
                  }`}
                  data-testid={`stem-row-${stem.type}`}
                >
                  {/* Icon */}
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${stemColor}20` }}
                  >
                    <Icon className="w-5 h-5" style={{ color: stemColor }} />
                  </div>

                  {/* Name */}
                  <span className="text-white font-medium w-24 text-sm">{stem.name}</span>

                  {/* Volume Slider */}
                  <div className="flex-1 mx-4">
                    <Slider
                      value={[stem.muted ? 0 : stem.volume]}
                      onValueChange={(val) => setVolume(i, val)}
                      max={100}
                      step={1}
                      className="cursor-pointer"
                      data-testid={`stem-volume-${stem.type}`}
                    />
                  </div>

                  {/* Volume % */}
                  <span className="text-zinc-400 text-xs w-12 text-right tabular-nums">
                    {stem.muted ? "MUTE" : `${stem.volume}%`}
                  </span>

                  {/* Mute toggle */}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => toggleMute(i)}
                    className={`${stem.muted ? "text-red-400" : "text-zinc-400"} hover:text-white`}
                    data-testid={`stem-mute-${stem.type}`}
                  >
                    {stem.muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                  </Button>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Save & Buy */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="flex flex-col sm:flex-row gap-4"
        >
          <div className="flex-1 flex gap-3">
            <Input
              value={mixName}
              onChange={(e) => setMixName(e.target.value)}
              placeholder="Name your mix..."
              className="bg-zinc-900 border-white/10"
              data-testid="mix-name-input"
            />
            <Button onClick={saveMix} variant="outline" className="border-neon-cyan text-neon-cyan hover:bg-neon-cyan hover:text-black whitespace-nowrap" data-testid="save-mix-btn">
              <Save className="w-4 h-4 mr-2" /> Save Mix
            </Button>
          </div>
          <Button
            onClick={() => {
              addToCart({
                item_id: beatData.beat_id, type: "beat", title: beatData.title,
                price: beatData.basic_price, license_type: "basic",
                image_url: beatData.image_url
              });
              toast.success("Added to cart!");
            }}
            className="bg-neon-cyan text-black font-bold px-8"
            data-testid="customizer-add-cart"
          >
            <ShoppingCart className="w-4 h-4 mr-2" /> Buy This Beat
          </Button>
        </motion.div>
      </div>
    </div>
  );
}
